import React, { useState, useEffect, useCallback } from "react";
import "./styles/guestFeedback.css";
import wellnessGreen from "./assets/wellness-green.jpg";
import atmantanLogo from "./assets/atmantan.svg";
import {
  verifyRoomGuest,
  verifyGuest,
  fetchItinerary,
  fetchAvailableActivities,
  enrollInActivity,
  submitTreatmentFeedback,
  submitGeneralFeedback,
  requestBuggy,
  requestHousekeeping,
  getStoredToken,
  storeSession,
  clearSession,
} from "./services/guestFeedbackApi";

/**
 * Atmantan — Guest QR Feedback
 * UI from design/AtmantanGuestFeedback.jsx; data via guest_feedback_api.
 */

const MAP_POIS = [
  { zone: "Wellness Pavilion", note: "Therapies & doctor consults", cabins: "Vaari · Nira · Dyaus" },
  { zone: "Kriya Pavilion", note: "Yogic kriyas & pranayama" },
  { zone: "Yoga Studio", note: "Morning flow & restorative sessions" },
  { zone: "Dining Pavilion", note: "Wellness cuisine" },
  { zone: "Buddha Grove", note: "Meditation & quiet reflection" },
  { zone: "Mulshi Lake Deck", note: "Sunrise views & sound healing" },
];
const HK_TYPES = ["Room cleaning", "Fresh towels", "Extra water", "Amenities refill", "Turndown service"];

// ---------- primitives ----------
function Logo({ light }) {
  return <img src={atmantanLogo} alt="ātmantan" className={"logo" + (light ? " light" : "")} />;
}
function Lotus({ size = 22, filled = false, tone }) {
  const stroke = tone || "var(--gold-deep)";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20c-4 0-7-2.4-7-5.6 0 0 2.2.4 3.6 1.7C7.2 13.4 8 10.6 12 7c4 3.6 4.8 6.4 3.4 9.1 1.4-1.3 3.6-1.7 3.6-1.7C19 17.6 16 20 12 20Z" fill={filled ? "var(--gold)" : "none"} stroke={stroke} strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M12 20c-2.4 0-4.3-2.2-4.3-5.2 0 0 2.4 1 4.3 4 1.9-3 4.3-4 4.3-4 0 3-1.9 5.2-4.3 5.2Z" fill={filled ? "var(--gold-deep)" : "none"} stroke={stroke} strokeWidth="0.9" strokeLinejoin="round" opacity={filled ? 0.35 : 1} />
    </svg>
  );
}

function NavIcon({ name, size = 26, className }) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    "aria-hidden": true,
  };

  switch (name) {
    case "home":
      return (
        <svg {...svgProps}>
          <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5z" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...svgProps}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M8 3v4M16 3v4M3 11h18" />
        </svg>
      );
    case "reflect":
      return <Lotus size={size} tone="currentColor" />;
    case "activities":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="5" r="2" />
          <path d="M12 7v2" />
          <path d="M7 11c2-1.2 3.5-1.2 5 0s3 1.2 5 0" />
          <path d="M8 20l4-8 4 8" />
          <path d="M9.5 20h5" />
        </svg>
      );
    case "map":
      return (
        <svg {...svgProps}>
          <path d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case "services":
      return (
        <svg {...svgProps}>
          <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
          <path d="M4 14a2 2 0 0 0 2 2h1v-5H5a1 1 0 0 0-1 1v2z" />
          <path d="M20 14a2 2 0 0 1-2 2h-1v-5h1a1 1 0 0 1 1 1v2z" />
        </svg>
      );
    default:
      return null;
  }
}
function Pill({ children }) { return <span className="pill">{children}</span>; }
function Meadow() {
  return (
    <div className="meadow" aria-hidden="true">
      <span className="sun" />
      <span className="flower" style={{ top: "62%", left: "18%" }} />
      <span className="flower" style={{ top: "72%", left: "78%" }} />
      <span className="flower sm" style={{ top: "54%", left: "60%" }} />
      <span className="flower sm" style={{ top: "80%", left: "40%" }} />
    </div>
  );
}
function DateBlock({ iso, time, align }) {
  const d = new Date(iso + "T00:00:00");
  const year = d.getFullYear();
  const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();
  return (
    <div className={"dateblock " + (align || "")}>
      <span className="db-year">{year}</span>
      <span className="db-day">{day}</span>
      <span className="db-time">{time}</span>
    </div>
  );
}
function NightsArrow({ nights }) {
  return (
    <div className="nights">
      <span className="nights-label">{nights} Nights</span>
      <svg className="nights-arrow" viewBox="0 0 72 16" aria-hidden="true">
        <line x1="0" y1="8" x2="54" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M54 4.5 L62 8 L54 11.5 Z" fill="currentColor" />
      </svg>
    </div>
  );
}
function Toast({ text, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, [text, onDone]);
  return <div className="toast" role="status">{text}</div>;
}

// ---------- app ----------
export default function GuestFeedbackApp({ roomNumber }) {
  const [screen, setScreen] = useState("landing");
  const [pickCandidates, setPickCandidates] = useState([]);
  const [pendingLastFour, setPendingLastFour] = useState("");
  const [profile, setProfile] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [pin, setPin] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [treatmentRatings, setTreatmentRatings] = useState({});
  const [activeTreatment, setActiveTreatment] = useState(null);
  const [generalRating, setGeneralRating] = useState(0);
  const [generalText, setGeneralText] = useState("");
  const [generalDone, setGeneralDone] = useState(false);
  const [toast, setToast] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const dayLabel = profile?.checkIn
    ? `Day ${Math.max(1, Math.floor((Date.parse(today) - Date.parse(profile.checkIn)) / 86400000) + 1)}`
    : "Today";

  const buggyPickups = profile?.room
    ? [`My Room (${profile.room})`, "Wellness Pavilion", "Dining Pavilion", "Reception", "Yoga Studio", "Mulshi Lake Deck"]
    : ["Wellness Pavilion", "Dining Pavilion", "Reception", "Yoga Studio", "Mulshi Lake Deck"];

  const loadItinerary = useCallback(async () => {
    try {
      const data = await fetchItinerary(today);
      setItinerary(data.items || []);
    } catch (err) {
      console.error(err);
      setItinerary([]);
    }
  }, [today]);

  const completeLogin = useCallback(async (result) => {
    storeSession(result.token, result.guest);
    setProfile(result.guest);
    setPickCandidates([]);
    setPendingLastFour("");
    setPin("");
    setAuthErr("");
    await loadItinerary();
    setScreen("home");
  }, [loadItinerary]);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;
    try {
      const raw = sessionStorage.getItem('guest_feedback_guest');
      if (raw) setProfile(JSON.parse(raw));
    } catch (_) { /* ignore */ }
    fetchItinerary(today)
      .then((data) => {
        setItinerary(data.items || []);
        setScreen('home');
      })
      .catch(() => clearSession());
  }, [today]);

  const eligible = itinerary.filter((i) => i.kind === "treatment" && i.feedbackEligible);

  function pressDigit(d) { setAuthErr(""); if (pin.length < 4) setPin(pin + d); }
  function resetToStart() {
    clearSession();
    setPickCandidates([]);
    setPendingLastFour("");
    setProfile(null);
    setItinerary([]);
    setPin("");
    setAuthErr("");
    setScreen("landing");
  }

  useEffect(() => {
    if (screen !== "landing" || pin.length !== 4 || verifying) return;
    const t = setTimeout(async () => {
      setVerifying(true);
      setAuthErr("");
      const code = pin;
      try {
        const result = await verifyRoomGuest({ room: roomNumber, lastFour: code });
        if (result.token) {
          await completeLogin(result);
        } else if (result.needsDisambiguation) {
          setPickCandidates(result.guests || []);
          setPendingLastFour(code);
          setPin("");
          setScreen("choose");
        }
      } catch (err) {
        const msg = err.message || "";
        if (msg === "Failed to fetch" || msg.includes("NetworkError")) {
          setAuthErr("Cannot reach the server. Start the backend on port 8000.");
        } else {
          setAuthErr(msg || "That doesn't match our records. Please try again.");
        }
        setPin("");
      } finally {
        setVerifying(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [pin, screen, roomNumber, verifying, completeLogin]);

  async function pickGuest(g) {
    if (verifying || !pendingLastFour) return;
    setVerifying(true);
    setAuthErr("");
    try {
      const result = await verifyGuest({
        guestId: g.guestId,
        lastFour: pendingLastFour,
        room: roomNumber,
      });
      await completeLogin(result);
    } catch (err) {
      setAuthErr(err.message || "Could not verify. Please try again.");
      setPickCandidates([]);
      setPendingLastFour("");
      setScreen("landing");
    } finally {
      setVerifying(false);
    }
  }

  function backToDialPad() {
    setPickCandidates([]);
    setPendingLastFour("");
    setPin("");
    setAuthErr("");
    setScreen("landing");
  }
  function nav(to) { return () => setScreen(to); }

  // ---------- screens ----------
  function PinKeypad() {
    return (
      <>
        <div className="pin-dots">{[0, 1, 2, 3].map((i) => <span key={i} className={"pin-dot" + (pin.length > i ? " filled" : "")} />)}</div>
        {authErr && <p className="err light">{authErr}</p>}
        <div className="keypad">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => <button key={d} className="key" onClick={() => pressDigit(d)} disabled={verifying}>{d}</button>)}
          <span />
          <button className="key" onClick={() => pressDigit("0")} disabled={verifying}>0</button>
          <button className="key key-fn" onClick={() => setPin(pin.slice(0, -1))} aria-label="Delete" disabled={verifying}>⌫</button>
        </div>
      </>
    );
  }

  function Landing() {
    return (
      <div className="screen bare">
        <div className="hero hero-clear hero-auth">
          <img className="hero-photo" src={wellnessGreen} alt="" />
          <div className="hero-content">
            <div className="hero-top"><Logo light /></div>
            <div className="hero-foot"><h1 className="hero-title sm">Namaste</h1></div>
          </div>
        </div>
        <div className="panel panel-auth">
          <p className="panel-lede center">Enter the last 4 digits of your registered mobile number.</p>
          <PinKeypad />
          <p className="fineprint light">Not you? Please ask reception for assistance.</p>
        </div>
      </div>
    );
  }

  function ChooseGuest() {
    return (
      <div className="screen bare">
        <div className="hero hero-clear hero-auth">
          <img className="hero-photo" src={wellnessGreen} alt="" />
          <div className="hero-content">
            <div className="hero-top"><Logo light /></div>
            <div className="hero-foot"><h1 className="hero-title sm">Who is this?</h1></div>
          </div>
        </div>
        <div className="panel panel-auth">
          <button className="back light" onClick={backToDialPad}>‹ Back</button>
          <p className="panel-lede">We found more than one guest with this number. Please select your name.</p>
          {authErr && <p className="err light">{authErr}</p>}
          <div className="stack">
            {pickCandidates.map((g) => (
              <button key={g.guestId} className="glass-row" onClick={() => pickGuest(g)} disabled={verifying}>
                <span className="row-init">{g.guestName.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                <span className="row-name">{g.guestName}</span>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function Home() {
    if (!profile) return null;
    const nextUp = itinerary.find((i) => !i.completed);
    return (
      <div className="screen bare home-screen">
        <div className="hero hero-clear">
          <img className="hero-photo" src={wellnessGreen} alt="" />
          <div className="hero-content">
            <div className="hero-top"><Logo light /><button className="lotus-btn" onClick={resetToStart} aria-label="Sign out"><Lotus size={26} filled /></button></div>
            <div className="hero-foot"><p className="hero-eyebrow">{dayLabel} · {profile.program}</p><h1 className="hero-title sm">Namaste, {profile.firstName}</h1></div>
          </div>
        </div>
        <div className="panel panel-home">
          <div className="panel-body">
            <div className="feature" onClick={nav("feedback")} role="button">
              <div><p className="eyebrow onp">Reflect on your day</p><p className="feature-title">Share how your sessions felt</p></div>
              <span className="feature-cta">Begin ›</span>
            </div>

            {nextUp && (
              <div className="glass next"><p className="eyebrow onp">Next on your journey</p><div className="next-row"><span className="next-time">{nextUp.start}</span><div><p className="next-title">{nextUp.title}</p><p className="onp-soft sm">{nextUp.location} · {nextUp.instructor}</p></div></div></div>
            )}

            <div className="tiles">
              <button className="tile" onClick={nav("itinerary")}><span className="tile-ico"><NavIcon name="calendar" size={26} /></span><span className="tile-label">Today's itinerary</span></button>
              <button className="tile" onClick={nav("activities")}><span className="tile-ico"><NavIcon name="activities" size={26} /></span><span className="tile-label">Activities</span></button>
              <button className="tile" onClick={nav("map")}><span className="tile-ico"><NavIcon name="map" size={26} /></span><span className="tile-label">Property map</span></button>
              <button className="tile" onClick={nav("services")}><span className="tile-ico"><NavIcon name="services" size={26} /></span><span className="tile-label">Guest services</span></button>
            </div>
          </div>

          <div className="stay-foot">
            <div className="stay">
              <DateBlock iso={profile.checkIn} time="13:00 hrs" />
              <NightsArrow nights={profile.nights || "—"} />
              <DateBlock iso={profile.checkOut} time="10:00 hrs" align="right" />
            </div>
            <div className="stay-meta">
              {profile.room && <span className="stay-pill">Room {profile.room}</span>}
              {profile.consultingDoctor && <span className="stay-pill">{profile.consultingDoctor}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function SubHead({ title, sub }) {
    return (
      <div className="subhead">
        <Meadow />
        <button className="back light" onClick={nav("home")}>‹ Home</button>
        <div className="pill-center"><Pill>{title}</Pill></div>
        {sub && <p className="subhead-sub">{sub}</p>}
      </div>
    );
  }

  function Itinerary() {
    return (
      <div className="screen">
        <SubHead title="Today's Itinerary" sub={`${dayLabel} · ${fmt(today)}`} />
        <div className="body">
          <div className="timeline">
            {itinerary.map((it, idx) => {
              const rated = treatmentRatings[it.id];
              return (
                <div key={it.id} className={"tl-item" + (it.completed ? " done" : "")}>
                  <div className="tl-rail"><span className={"tl-node" + (it.completed ? " done" : "")}>{it.completed ? <Lotus size={18} filled /> : <span className="tl-dot" />}</span>{idx < itinerary.length - 1 && <span className="tl-thread" />}</div>
                  <div className="tl-body">
                    <div className="tl-time">{it.start}<span className="tl-end"> – {it.end}</span></div>
                    <p className="tl-title">{it.title}</p>
                    <p className="muted sm">{itemMeta(it)}</p>
                    {it.feedbackEligible && <button className={"inline-fb" + (rated ? " done" : "")} onClick={() => { setActiveTreatment(it); setScreen("feedback"); }}>{rated ? `Reflected · ${rated.rating}/5` : "Share feedback ›"}</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  function Feedback() {
    if (activeTreatment) return <TreatmentFeedback />;
    return (
      <div className="screen">
        <SubHead title="Reflect" sub="Your voice shapes the Atmantan Way" />
        <div className="body">
          {eligible.length > 0 && (
            <div className="stack feedback-sessions">
              {eligible.map((t) => {
                const rated = treatmentRatings[t.id];
                return (
                  <button key={t.id} className={"card-row" + (rated ? " done" : "")} onClick={() => setActiveTreatment(t)}>
                    <div><p className="card-row-title">{t.title}</p><p className="muted sm">{t.start} · {t.instructor}</p></div>
                    {rated ? <span className="badge">{rated.rating}/5</span> : <span className="chev dark">›</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div className="card">
            {generalDone ? (
              <div className="thanks"><Lotus size={30} filled /><p className="thanks-title">Thank you, {profile?.firstName}.</p><p className="muted">Your reflection has been shared with our team.</p></div>
            ) : (
              <>
                <p className="q">How has your journey felt so far?</p>
                <Rating value={generalRating} onChange={setGeneralRating} />
                <label className="ta-label">Anything you'd like us to know</label>
                <textarea className="ta" placeholder="Type here." value={generalText} onChange={(e) => setGeneralText(e.target.value)} />
                <button className="btn" disabled={!generalRating} onClick={async () => {
                  try {
                    await submitGeneralFeedback({ rating: generalRating, comment: generalText });
                    setGeneralDone(true);
                    setToast("General feedback shared. Thank you.");
                  } catch (err) {
                    setToast(err.message || "Could not send feedback");
                  }
                }}>Share reflection</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  function TreatmentFeedback() {
    return (
      <TreatmentFeedbackPanel
        treatment={activeTreatment}
        existing={treatmentRatings[activeTreatment.id]}
        onBack={() => setActiveTreatment(null)}
        onSubmit={async (r, c) => {
          const t = activeTreatment;
          await submitTreatmentFeedback({
            bookingKey: t.bookingKey || t.id,
            rating: r,
            comment: c,
          });
          setTreatmentRatings({ ...treatmentRatings, [t.id]: { rating: r, comment: c } });
          setActiveTreatment(null);
          setToast(`Feedback on ${t.title} shared. Thank you.`);
        }}
        onError={(msg) => setToast(msg)}
        SubHead={SubHead}
        Rating={Rating}
      />
    );
  }

  function Activities() {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [joining, setJoining] = useState(false);
    const [joinErr, setJoinErr] = useState("");

    const loadActivities = useCallback(() => {
      setLoading(true);
      return fetchAvailableActivities(today)
        .then((data) => {
          setActivities(data.activities || []);
          setError("");
        })
        .catch((e) => {
          setError(e.message || "Could not load activities");
          setActivities([]);
        })
        .finally(() => setLoading(false));
    }, [today]);

    useEffect(() => {
      loadActivities();
    }, [loadActivities]);

    async function handleJoin(activity) {
      setJoining(true);
      setJoinErr("");
      try {
        const result = await enrollInActivity(activity.id, today);
        setToast({ message: result.message || `Registered for ${activity.title}` });
        await Promise.all([loadActivities(), loadItinerary()]);
        setSelected(null);
      } catch (e) {
        setJoinErr(e.message || "Could not register");
      } finally {
        setJoining(false);
      }
    }

    function rowClass(a) {
      let cls = "act-row";
      if (a.isEnrolled) cls += " enrolled";
      else if (a.hasClash) cls += " clash";
      else if (a.isFull) cls += " full";
      return cls;
    }

    function statusNote(a) {
      if (a.isEnrolled) return "Registered";
      if (a.hasClash && a.clashesWith?.length) {
        const c = a.clashesWith[0];
        return `Clashes with ${c.title}`;
      }
      if (a.isFull) return "Full";
      return null;
    }

    const sel = selected;

    return (
      <div className="screen">
        <SubHead title="Today's Activities" sub="Open sessions you may join" />
        <div className="body">
          <div className="stack">
            {loading && <p className="muted sm">Loading activities…</p>}
            {error && <p className="muted sm">{error}</p>}
            {!loading && !error && activities.length === 0 && (
              <p className="muted sm">No activities scheduled for today.</p>
            )}
            {activities.map((a) => {
              const note = statusNote(a);
              return (
                <button key={a.id} type="button" className={rowClass(a)} onClick={() => { setJoinErr(""); setSelected(a); }}>
                  <span className="act-time">{a.start}</span>
                  <div className="act-body">
                    <p className="card-row-title">{a.title}</p>
                    <p className="muted sm">{a.location} · {a.level}</p>
                    {note && <p className="act-note">{note}</p>}
                  </div>
                  <span className="spots">{a.spots}</span>
                </button>
              );
            })}
          </div>
        </div>

        {sel && (
          <div className="act-overlay" onClick={() => setSelected(null)} role="presentation">
            <div className="act-detail" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <button type="button" className="act-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
              <p className="act-detail-time">{sel.start} – {sel.end} · {sel.durationMinutes} min</p>
              <h3 className="act-detail-title">{sel.title}</h3>
              <p className="muted sm">{sel.location}</p>
              <div className="act-detail-grid">
                <div><p className="act-detail-label">Instructor{sel.instructors?.length > 1 ? "s" : ""}</p><p>{(sel.instructors || [sel.instructor]).filter(Boolean).join(", ") || "—"}</p></div>
                <div><p className="act-detail-label">Intensity</p><p>{sel.level}</p></div>
                <div><p className="act-detail-label">Spaces</p><p>{sel.spots}{sel.spotsRemaining != null ? ` (${sel.spotsRemaining} left)` : ""}</p></div>
              </div>
              {sel.description && (
                <div className="act-detail-desc">
                  <p className="act-detail-label">About</p>
                  <p>{sel.description}</p>
                </div>
              )}
              {sel.hasClash && sel.clashesWith?.length > 0 && (
                <p className="act-clash-msg">Overlaps with {sel.clashesWith.map((c) => `${c.title} (${c.start})`).join(", ")}</p>
              )}
              {joinErr && <p className="act-join-err">{joinErr}</p>}
              <button
                type="button"
                className="btn primary act-join-btn"
                disabled={!sel.canJoin || joining}
                onClick={() => handleJoin(sel)}
              >
                {sel.isEnrolled ? "You're registered" : sel.isFull ? "Activity full" : sel.hasClash ? "Schedule clash" : joining ? "Joining…" : "Join activity"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function MapScreen() {
    return (
      <div className="screen">
        <SubHead title="Property Map" sub="42 acres overlooking Mulshi Lake" />
        <div className="body">
          <div className="map">
            <div className="map-hill" /><div className="map-lake">Mulshi Lake</div>
            <span className="map-pin" style={{ top: "22%", left: "30%" }}>Wellness</span>
            <span className="map-pin" style={{ top: "40%", left: "62%" }}>Kriya</span>
            <span className="map-pin" style={{ top: "58%", left: "24%" }}>Dining</span>
            <span className="map-pin gold" style={{ top: "34%", left: "44%" }}>You · {profile?.room || roomNumber}</span>
          </div>
          <div className="stack">
            {MAP_POIS.map((p) => <div key={p.zone} className="card sm"><p className="card-row-title">{p.zone}</p><p className="muted sm">{p.note}{p.cabins ? ` · ${p.cabins}` : ""}</p></div>)}
          </div>
        </div>
      </div>
    );
  }

  function Services() {
    const [buggyFrom, setBuggyFrom] = useState(buggyPickups[0]);
    const [buggyWhen, setBuggyWhen] = useState("As soon as possible");
    const [hkType, setHkType] = useState(HK_TYPES[0]);
    const [hkUrgent, setHkUrgent] = useState(false);
    return (
      <div className="screen">
        <SubHead title="Guest Services" sub="We're here whenever you need us" />
        <div className="body">
          <div className="card">
            <p className="q sm">Request a buggy</p>
            <label className="fld">Pick-up point</label>
            <select className="select" value={buggyFrom} onChange={(e) => setBuggyFrom(e.target.value)}>{buggyPickups.map((p) => <option key={p}>{p}</option>)}</select>
            <label className="fld">When</label>
            <select className="select" value={buggyWhen} onChange={(e) => setBuggyWhen(e.target.value)}><option>As soon as possible</option><option>In 15 minutes</option><option>In 30 minutes</option></select>
            <button className="btn" onClick={async () => {
              try {
                await requestBuggy({ pickup: buggyFrom, when: buggyWhen });
                setToast(`Buggy requested from ${buggyFrom}. On its way.`);
              } catch (err) {
                setToast(err.message || "Request failed");
              }
            }}>Request buggy</button>
          </div>
          <div className="card">
            <p className="q sm">Housekeeping</p>
            <label className="fld">What do you need?</label>
            <select className="select" value={hkType} onChange={(e) => setHkType(e.target.value)}>{HK_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
            <button className={"toggle" + (hkUrgent ? " on" : "")} onClick={() => setHkUrgent(!hkUrgent)}><span className="knob" /> Mark as urgent</button>
            <button className="btn" onClick={async () => {
              try {
                await requestHousekeeping({ requestType: hkType, urgent: hkUrgent });
                setToast(`Housekeeping request sent — ${hkType}.`);
              } catch (err) {
                setToast(err.message || "Request failed");
              }
            }}>Send request</button>
          </div>
        </div>
      </div>
    );
  }

  function Rating({ value, onChange, big }) {
    const labels = ["", "Could be gentler", "Fair", "Good", "Wonderful", "Transformative"];
    return (
      <div className="rating-wrap">
        <div className={"rating" + (big ? " big" : "")}>{[1, 2, 3, 4, 5].map((n) => <button key={n} className={"pip" + (value >= n ? " on" : "")} onClick={() => onChange(n)} aria-label={`${n} of 5`}><Lotus size={big ? 38 : 30} filled={value >= n} /></button>)}</div>
        <p className="rating-label">{value ? labels[value] : "Tap to rate"}</p>
      </div>
    );
  }

  const showTabs = ["home", "itinerary", "feedback", "map", "services"].includes(screen);
  return (
    <div className="stage">
      <div className="phone">
        <div className={"phone-inner" + (showTabs ? " with-tabbar" : "")}>
          {screen === "landing" && <Landing />}
          {screen === "choose" && <ChooseGuest />}
          {screen === "home" && <Home />}
          {screen === "itinerary" && <Itinerary />}
          {screen === "feedback" && <Feedback />}
          {screen === "activities" && <Activities />}
          {screen === "map" && <MapScreen />}
          {screen === "services" && <Services />}
        </div>
        {showTabs && (
          <nav className="tabbar">
            <TabBtn id="home" cur={screen} set={setScreen} label="Home" icon={<NavIcon name="home" size={24} />} onGo={() => setActiveTreatment(null)} />
            <TabBtn id="itinerary" cur={screen} set={setScreen} label="Today" icon={<NavIcon name="calendar" size={24} />} />
            <TabBtn id="feedback" cur={screen} set={setScreen} label="Reflect" icon={<NavIcon name="reflect" size={24} />} onGo={() => setActiveTreatment(null)} />
            <TabBtn id="map" cur={screen} set={setScreen} label="Map" icon={<NavIcon name="map" size={24} />} />
            <TabBtn id="services" cur={screen} set={setScreen} label="Services" icon={<NavIcon name="services" size={24} />} />
          </nav>
        )}
        {toast && <Toast text={toast} onDone={() => setToast(null)} />}
      </div>
    </div>
  );
}

function TabBtn({ id, cur, set, label, icon, onGo }) {
  return <button className={"tab" + (cur === id ? " active" : "")} onClick={() => { onGo && onGo(); set(id); }}><span className="tab-ico">{icon}</span><span className="tab-label">{label}</span></button>;
}

function TreatmentFeedbackPanel({ treatment, existing, onBack, onSubmit, onError, SubHead, Rating }) {
  const [r, setR] = useState(existing?.rating || 0);
  const [c, setC] = useState(existing?.comment || "");
  const t = treatment;
  return (
    <div className="screen">
      <SubHead title={t.title} sub={`${t.start} · ${t.location} · ${t.instructor}`} />
      <div className="body">
        <button className="back dark" onClick={onBack}>‹ All sessions</button>
        <div className="card">
          <p className="q">How did this session feel?</p>
          <Rating value={r} onChange={setR} big />
          <textarea className="ta" placeholder="Share a little about your experience (optional)." value={c} onChange={(e) => setC(e.target.value)} />
          <button className="btn" disabled={!r} onClick={async () => {
            try {
              await onSubmit(r, c);
            } catch (err) {
              onError(err.message || "Could not send feedback");
            }
          }}>{existing ? "Update reflection" : "Share reflection"}</button>
        </div>
      </div>
    </div>
  );
}

function fmt(iso) { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
function itemMeta(it) {
  return [cap(it.kind), it.location, it.instructor].filter(Boolean).join(" · ");
}

